import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { CheckCircle2, Phone, ArrowRight, Award, Users, Shield, Heart } from 'lucide-react';

const AboutPage = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Pierce Premier Cleaning Services",
    "description": "Learn about Heather and Bob Pierce, the husband-and-wife owners of Pierce Premier Cleaning Services in Brevard County, Florida.",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "Pierce Premier Cleaning Services",
      "description": "Locally owned husband-and-wife cleaning company serving Brevard County, Florida",
      "telephone": "+1-321-408-1347",
      "areaServed": {
        "@type": "State",
        "name": "Florida"
      }
    }
  };

  return (
    <>
      <SEO 
        title="About Us | Pierce Premier Cleaning Services | Brevard County, FL"
        description="Meet Heather and Bob Pierce, the husband-and-wife team behind Pierce Premier Cleaning Services. Serving Brevard County with 15+ years of executive housekeeping experience."
        canonical="/about"
        structuredData={structuredData}
      />

      <div className="min-h-screen pt-20 bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-gray-50 py-24 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16">
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Local Owners. Personally Invested.
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The story behind Pierce Premier Cleaning Services
              </p>
            </div>

            {/* Owners Photo and Story */}
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_pierce-premier/artifacts/4o463qfq_image.png"
                  alt="Heather and Bob Pierce, Owners of Pierce Premier Cleaning Services"
                  className="rounded-2xl shadow-2xl w-full"
                />
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Meet Heather & Bob
                </h2>
                
                <p className="text-lg text-gray-700 leading-relaxed">
                  Pierce Premier Cleaning Services was built on a simple belief: cleaning should be personal, consistent, and trustworthy.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed">
                  Heather brings over 15 years of executive housekeeping experience to every job. Together with Bob, they've created a company where you know exactly who you're working with every time.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed">
                  We're not a large company managing multiple crews across Florida. We're a local husband-and-wife team committed to delivering the same high-quality service, every single time.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed">
                  When you hire Pierce Premier, you get reliable, professional service with a personal touch that only local ownership can provide.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why We're Different */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                What Makes Us Different
              </h2>
              <p className="text-xl text-gray-600">
                Local ownership means consistency, accountability, and trust
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Husband & Wife Owned
                  </h3>
                  <p className="text-gray-600">
                    A true family business where we stand behind every job personally
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Award className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    15+ Years Experience
                  </h3>
                  <p className="text-gray-600">
                    Executive housekeeping expertise in every cleaning
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Fully Insured
                  </h3>
                  <p className="text-gray-600">
                    Complete protection and peace of mind for your property
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Pet-Friendly
                  </h3>
                  <p className="text-gray-600">
                    We love pets and treat them like family
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Our Approach */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our Approach
              </h2>
            </div>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Personal Service</h3>
                  <p className="text-gray-700 leading-relaxed">
                    When you hire us, you're working directly with the owners. No rotating crews, no surprises. Just consistent, reliable service from people who care about your home.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Attention to Detail</h3>
                  <p className="text-gray-700 leading-relaxed">
                    With over 15 years of executive housekeeping experience, Heather brings a level of precision and care that shows in every corner we clean.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Local Commitment</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We live here, work here, and build our reputation here in Brevard County. Your satisfaction isn't just good business—it's personal.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Clear Communication</h3>
                  <p className="text-gray-700 leading-relaxed">
                    From your first quote to the finished job, you'll always know what to expect. We believe in honest, straightforward communication.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl mb-10 text-blue-50">
              Get a personalized quote and see why Brevard County families trust Pierce Premier
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-10 py-7">
                  Request Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:3214081347">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-7">
                  <Phone className="mr-2 w-5 h-5" />
                  Call 321-408-1347
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;
